import React, { useEffect, useRef, useState } from 'react';

// Preferência global do aluno em localStorage: o painel sempre reabre no tamanho escolhido
const PANEL_SIZE_STORAGE_KEY = 'simuladoNotesPanelSize';

export const MOBILE_BREAKPOINT = 768;
// Espelha o width do DrawerPanel em styles.ts (fallback quando o painel não está montado)
const DEFAULT_WIDTH = 650;
const MIN_WIDTH = 320;
const MIN_HEIGHT = 280;
const WINDOW_WIDTH_MARGIN = 48;
const WINDOW_HEIGHT_MARGIN = 96;
const KEYBOARD_RESIZE_STEP = 24;

export interface PanelSize {
  width?: number;
  height?: number;
}

// Mesma política dos outros storages do painel: storage pode lançar (Safari em navegação
// privada, política de bloqueio); o redimensionamento degrada para "só nesta sessão".
const safeGetPanelSize = (): PanelSize => {
  try {
    const raw = localStorage.getItem(PANEL_SIZE_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return {
      width: typeof parsed?.width === 'number' ? parsed.width : undefined,
      height: typeof parsed?.height === 'number' ? parsed.height : undefined,
    };
  } catch {
    return {};
  }
};

const safeSetPanelSize = (size: PanelSize) => {
  try {
    localStorage.setItem(PANEL_SIZE_STORAGE_KEY, JSON.stringify(size));
  } catch {
    // sem storage disponível: o tamanho vive só no estado do componente
  }
};

// Limites: mínimo utilizável e máximo que ainda deixa uma faixa da prova visível
const maxWidth = () => Math.max(window.innerWidth - WINDOW_WIDTH_MARGIN, MIN_WIDTH);
const maxHeight = () => Math.max(window.innerHeight - WINDOW_HEIGHT_MARGIN, MIN_HEIGHT);

const clampWidth = (width: number) => Math.min(Math.max(width, MIN_WIDTH), maxWidth());

const clampHeight = (height: number) => Math.min(Math.max(height, MIN_HEIGHT), maxHeight());

const isMobileViewport = () =>
  typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT;

// Altura padrão do bottom sheet mobile, espelhando o min(72vh, 640px) do CSS
const defaultMobileHeight = () => Math.min(window.innerHeight * 0.72, 640);

export function useResizablePanel() {
  const [size, setSize] = useState<PanelSize>(() => safeGetPanelSize());
  const [isMobile, setIsMobile] = useState(isMobileViewport);
  const panelRef = useRef<HTMLElement | null>(null);
  const draggingRef = useRef(false);
  // Valor aplicado direto no DOM durante o arraste; estado/storage só ao soltar o ponteiro
  const pendingRef = useRef<number | null>(null);
  const mountedRef = useRef(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(isMobileViewport());
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  // Persiste fora do updater do setState (updaters devem ser puros); pula o valor do mount
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    safeSetPanelSize(size);
  }, [size]);

  const commitSize = (value: number) => {
    // Largura (desktop/tablet) e altura (mobile) são preferências independentes
    setSize((prev) => (isMobile ? { ...prev, height: value } : { ...prev, width: value }));
  };

  // Tamanho vigente: preferência salva ou, sem ela, a medida real do painel renderizado
  // (com fallback nas constantes que espelham o CSS, para ambientes sem layout)
  const currentValue = () => {
    const rect = panelRef.current?.getBoundingClientRect();
    if (isMobile) return size.height ?? (rect?.height || defaultMobileHeight());
    return size.width ?? (rect?.width || DEFAULT_WIDTH);
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    draggingRef.current = true;
    pendingRef.current = null;
    try {
      e.currentTarget.setPointerCapture?.(e.pointerId);
    } catch {
      // ambientes sem pointer capture: o arraste segue funcionando dentro do handle
    }
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return;
    e.preventDefault();

    // Painel ancorado à direita (desktop) ou embaixo (mobile): o novo tamanho é a
    // distância entre o ponteiro e a borda oposta da janela
    const next = isMobile
      ? clampHeight(window.innerHeight - e.clientY)
      : clampWidth(window.innerWidth - e.clientX);
    pendingRef.current = next;

    const panel = panelRef.current;
    if (panel) {
      if (isMobile) panel.style.height = `${next}px`;
      else panel.style.width = `${next}px`;
    }
  };

  const handlePointerEnd = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    try {
      e.currentTarget.releasePointerCapture?.(e.pointerId);
    } catch {
      // ponteiro já liberado ou sem suporte a capture: nada a fazer
    }
    if (pendingRef.current !== null) commitSize(pendingRef.current);
    pendingRef.current = null;
  };

  // Acessibilidade: o handle também redimensiona pelo teclado (setas)
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const grow = isMobile ? e.key === 'ArrowUp' : e.key === 'ArrowLeft';
    const shrink = isMobile ? e.key === 'ArrowDown' : e.key === 'ArrowRight';
    if (!grow && !shrink) return;

    e.preventDefault();
    const delta = grow ? KEYBOARD_RESIZE_STEP : -KEYBOARD_RESIZE_STEP;
    const next = currentValue() + delta;
    commitSize(isMobile ? clampHeight(next) : clampWidth(next));
  };

  // Sem preferência salva, nenhum estilo inline: valem os tamanhos padrão do CSS
  const panelStyle: React.CSSProperties | undefined = isMobile
    ? size.height !== undefined
      ? { height: clampHeight(size.height) }
      : undefined
    : size.width !== undefined
      ? { width: clampWidth(size.width) }
      : undefined;

  return {
    panelRef,
    panelStyle,
    isMobile,
    resizeHandleProps: {
      onPointerDown: handlePointerDown,
      onPointerMove: handlePointerMove,
      onPointerUp: handlePointerEnd,
      onPointerCancel: handlePointerEnd,
      onKeyDown: handleKeyDown,
      role: 'separator' as const,
      'aria-orientation': (isMobile ? 'horizontal' : 'vertical') as 'horizontal' | 'vertical',
      'aria-label': 'Redimensionar painel de anotações',
      'aria-valuenow': Math.round(currentValue()),
      'aria-valuemin': isMobile ? MIN_HEIGHT : MIN_WIDTH,
      'aria-valuemax': Math.round(isMobile ? maxHeight() : maxWidth()),
      tabIndex: 0,
    },
  };
}
